import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useImperativeHandle, forwardRef } from 'react';
import MultiSelectTicker from '../MultiSelectTicker';
import ScreenerSavedParams from '../ScreenerSavedParams';
import { BookmarkPlus, PlusCircle, AlertTriangle, Play } from 'lucide-react';
import {
  intradayPresets,
  buildIntradayScenarios,
  buildZeroDTETargetScenarios,
} from '../../utils/presetMappings';
import { getOptionsChain } from '../../services/api';
import { capToOneTicker } from './ZeroDTEForm';
import TargetPriceStrikeSection from './TargetPriceStrikeSection';
import '../../styles/MobileScreenerForm.css';

// Mobile 0DTE form — short-dated (0DTE → ~3 months), ONE ticker at a time.
// Mirrors the mobile Intermediate form but reads intradayPresets and emits
// scenarios via buildIntradayScenarios (explicit expiration window +
// _intraday metadata).
const directionLabels = {
  buy_call:  { title: 'Bullish',      subtitle: 'Buy calls' },
  buy_put:   { title: 'Bearish',      subtitle: 'Buy puts' },
  sell_put:  { title: 'Neutral/bull', subtitle: 'Sell puts' },
  sell_call: { title: 'Neutral/bear', subtitle: 'Sell calls', warning: 'High risk — unlimited loss' },
};

const MobileZeroDTEForm = forwardRef(({
  onAddToQueue,
  onAddAndRun,
  onSaveScenario,
  queueCount,
  loading,
  savedParams = [],
  onLoadParam,
  onDeleteParam,
  onAddParamToQueue,
}, ref) => {
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [direction, setDirection] = useState(null);
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [selectedStrikes, setSelectedStrikes] = useState([]);
  const [isPresetSheetOpen, setIsPresetSheetOpen] = useState(false);

  // Strike positioning sub-mode: moneyness bands (default) or an absolute
  // target price with a buffer around it.
  const [strikeMode, setStrikeMode] = useState('bands'); // 'bands' | 'target'
  const [targetPrice, setTargetPrice] = useState('');
  const [bufferPct, setBufferPct] = useState(null); // null = exact target
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);

  const activeTicker = selectedTickers.length > 0 ? selectedTickers[0].value : null;

  // loadScenario restores a ticker and its target price together; the
  // ticker-change effect below must not wipe the target it just set.
  const pendingTargetRef = useRef(null);
  // The ticker we last fired a quote request for — stops a failed fetch from
  // retrying in a loop, and self-invalidates when the ticker changes.
  const attemptedTickerRef = useRef(null);

  const fetchCurrentPrice = useCallback(async (ticker) => {
    if (!ticker) return;
    setPriceLoading(true);
    setPriceError(null);
    try {
      const data = await getOptionsChain(ticker);
      const price = parseFloat(data?.currentPrice);
      if (Number.isFinite(price) && price > 0) {
        setCurrentPrice(price);
      } else {
        setCurrentPrice(null);
        setPriceError('unavailable');
      }
    } catch (err) {
      setCurrentPrice(null);
      setPriceError(err?.message || 'failed');
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // A different underlying invalidates both the quote and any target derived
  // from it.
  useEffect(() => {
    setCurrentPrice(null);
    setPriceError(null);
    if (pendingTargetRef.current != null) {
      setTargetPrice(pendingTargetRef.current);
      pendingTargetRef.current = null;
    } else {
      setTargetPrice('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTicker]);

  // Only the target-price mode needs a live quote.
  useEffect(() => {
    if (strikeMode !== 'target' || !activeTicker) return;
    if (attemptedTickerRef.current === activeTicker) return;
    attemptedTickerRef.current = activeTicker;
    fetchCurrentPrice(activeTicker);
  }, [strikeMode, activeTicker, fetchCurrentPrice]);

  const handleRetryPrice = useCallback(() => {
    attemptedTickerRef.current = activeTicker;
    fetchCurrentPrice(activeTicker);
  }, [activeTicker, fetchCurrentPrice]);

  // Pill-row refs for first-render scroll centering on the ATM anchor
  const expirationRowRef = useRef(null);
  const strikeRowRef = useRef(null);

  // Center the anchor pill (data-default-pill) on first render for each row.
  // Pure scroll-position adjustment; nothing is selected.
  useLayoutEffect(() => {
    const centerAnchor = (container) => {
      if (!container) return;
      const anchor = container.querySelector('[data-default-pill="true"]');
      if (!anchor) return;
      const target = anchor.offsetLeft + anchor.offsetWidth / 2 - container.offsetWidth / 2;
      container.scrollLeft = Math.max(0, target);
    };
    centerAnchor(expirationRowRef.current);
    centerAnchor(strikeRowRef.current);
  }, []);

  const isPut = direction?.includes('put');

  // Target mode needs a valid target AND a live quote to convert it into the
  // moneyness the backend expects.
  const isTargetStrikeReady = () => {
    const target = parseFloat(targetPrice);
    return Number.isFinite(target) && target > 0 && Number.isFinite(currentPrice) && currentPrice > 0;
  };

  const isFormValid = () =>
    selectedTickers.length > 0 &&
    direction !== null &&
    selectedExpirations.length > 0 &&
    (strikeMode === 'target' ? isTargetStrikeReady() : selectedStrikes.length > 0);

  const buildScenarios = () => {
    if (!isFormValid()) return [];
    if (strikeMode === 'target') {
      return buildZeroDTETargetScenarios(
        { expirations: selectedExpirations, targetPrice, bufferPct, currentPrice },
        selectedTickers,
        direction,
      );
    }
    const isBullishPosition = direction === 'buy_call' || direction === 'sell_put';
    const moveSeed = isBullishPosition ? 'rally' : 'correction';
    return buildIntradayScenarios(
      {
        expirations: selectedExpirations,
        strikes: selectedStrikes,
        speeds: ['balanced'],
        moves: [moveSeed],
      },
      selectedTickers,
      direction,
    );
  };

  useImperativeHandle(ref, () => ({
    getFormState: () => ({
      selectedTickers,
      direction,
      selectedExpirations,
      selectedStrikes,
      strikeMode,
      targetPrice,
      bufferPct,
      isValid: isFormValid(),
    }),
    buildScenarios,
    isFormValid,
    resetForm: () => {
      setSelectedTickers([]);
      setDirection(null);
      setSelectedExpirations([]);
      setSelectedStrikes([]);
      setStrikeMode('bands');
      setTargetPrice('');
      setBufferPct(null);
    },
    loadScenario: (scenario) => {
      const tickerArray = Array.isArray(scenario.tickers)
        ? scenario.tickers
        : (scenario.tickers || '').split(',').map(t => t.trim()).filter(Boolean);
      setSelectedTickers(capToOneTicker(tickerArray.map(t => ({ value: t, label: t }))));

      const positionType = scenario.position_type || 'buy';
      const optionType = scenario.option_type || 'call';
      setDirection(`${positionType}_${optionType}`);

      if (scenario._intraday?.strikeMode === 'target') {
        setSelectedExpirations([scenario._intraday.expiration]);
        setStrikeMode('target');
        setBufferPct(scenario._intraday.bufferPct ?? null);
        // Survives the ticker-change effect that would otherwise clear it.
        pendingTargetRef.current = String(scenario._intraday.targetPrice ?? '');
        setTargetPrice(String(scenario._intraday.targetPrice ?? ''));
      } else if (scenario._intraday) {
        setSelectedExpirations([scenario._intraday.expiration]);
        setSelectedStrikes([scenario._intraday.strike]);
        setStrikeMode('bands');
      } else {
        // Closest preset match
        let bestExp = null;
        let bestExpDiff = Infinity;
        for (const [key, p] of Object.entries(intradayPresets.expiration)) {
          const d = Math.abs(p.days_until_exp - scenario.days_until_exp);
          if (d < bestExpDiff) { bestExpDiff = d; bestExp = key; }
        }
        const adjStrike = optionType === 'put' && scenario.strike_pct !== 0 ? -scenario.strike_pct : scenario.strike_pct;
        let bestStrike = null;
        let bestStrikeDiff = Infinity;
        for (const [key, p] of Object.entries(intradayPresets.strike)) {
          const d = Math.abs(p.strike_pct - adjStrike);
          if (d < bestStrikeDiff) { bestStrikeDiff = d; bestStrike = key; }
        }
        if (bestExp) setSelectedExpirations([bestExp]);
        if (bestStrike) setSelectedStrikes([bestStrike]);
      }
    },
  }));

  const toggle = (arr, setArr, key) => {
    setArr(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleAddToQueue = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddToQueue) {
      scenarios.forEach(s => onAddToQueue(s));
    }
  };

  const handleRunNow = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddAndRun) {
      onAddAndRun(scenarios);
    }
  };

  const handleSave = async () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onSaveScenario) {
      for (const s of scenarios) {
        const ok = await onSaveScenario(s);
        if (!ok) break;
      }
    }
  };

  // Adjust the description sign for put positions (intraday UI shows the same
  // buckets but the API-side strike is flipped for puts).
  const adjustDescription = (description) => {
    if (!isPut || !description) return description;
    if (description.startsWith('-')) return '+' + description.slice(1);
    if (description.startsWith('+')) return '-' + description.slice(1);
    return description;
  };

  // Target mode emits one scenario per expiration (its strikes come from the
  // band around the target, not from the selection grid).
  const totalScenarios = !isFormValid()
    ? 0
    : strikeMode === 'target'
      ? selectedExpirations.length
      : selectedExpirations.length * selectedStrikes.length;

  return (
    <div className="msf-root">
      <div className="msf-form-header">
        <button
          type="button"
          className="msf-load-preset-btn"
          onClick={() => setIsPresetSheetOpen(true)}
        >
          <BookmarkPlus className="msf-load-preset-btn-icon" />
          Load preset
        </button>
      </div>

      {/* Tickers */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Select 1 Ticker</h3>
          {selectedTickers.length > 0 ? (
            <span className="msf-section-active">{selectedTickers[0].value}</span>
          ) : (
            <span className="msf-section-count">None selected</span>
          )}
        </div>
        <div className="msf-ticker-input-wrap">
          <MultiSelectTicker
            value={selectedTickers}
            onChange={(tickers) => setSelectedTickers(capToOneTicker(tickers))}
            placeholder="Add ticker..."
            controlBackground="#1f2937"
          />
        </div>
      </div>

      {/* Market direction */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Market direction</h3>
          {direction ? (
            <span className="msf-section-active">{directionLabels[direction].title}</span>
          ) : (
            <span className="msf-section-count">None selected</span>
          )}
        </div>
        <div className="msf-preset-card-grid">
          {Object.entries(directionLabels).map(([key, info]) => {
            const isSelected = direction === key;
            const fullTooltip = info.warning
              ? `${info.warning} — naked calls have unlimited loss potential. Only use if you fully understand the risks.`
              : undefined;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setDirection(key)}
                className={`msf-preset-card ${isSelected ? 'msf-selected' : ''}`}
                title={fullTooltip}
              >
                <span className="msf-preset-card-title">
                  {info.title}
                  {info.warning && (
                    <AlertTriangle
                      style={{ width: 12, height: 12, color: '#f97316', marginLeft: 6, flexShrink: 0 }}
                    />
                  )}
                </span>
                <span className="msf-preset-card-subtitle">{info.subtitle}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Expiration window */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Expiration window</h3>
          <div className="msf-section-meta">
            {selectedExpirations.length > 0 ? (
              <>
                <span className="msf-section-active">
                  {selectedExpirations.length === 1
                    ? intradayPresets.expiration[selectedExpirations[0]].label
                    : `${selectedExpirations.length} selected`}
                </span>
                <button type="button" className="msf-section-clear" onClick={() => setSelectedExpirations([])}>
                  Clear
                </button>
              </>
            ) : (
              <span className="msf-section-count">None selected</span>
            )}
          </div>
        </div>
        <div className="msf-preset-card-grid">
          {Object.entries(intradayPresets.expiration).map(([key, preset]) => {
            const isSelected = selectedExpirations.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(selectedExpirations, setSelectedExpirations, key)}
                className={`msf-preset-card ${isSelected ? 'msf-selected' : ''}`}
                title={preset.tooltip}
              >
                <span className="msf-preset-card-title">{preset.label}</span>
                <span className="msf-preset-card-subtitle">{preset.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Strike positioning */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Strike positioning</h3>
          <div className="msf-section-meta">
            {strikeMode === 'target' ? (
              <span className={parseFloat(targetPrice) > 0 ? 'msf-section-active' : 'msf-section-count'}>
                {parseFloat(targetPrice) > 0
                  ? `$${parseFloat(targetPrice)}${bufferPct != null ? ` ±${+(bufferPct * 100).toFixed(2)}%` : ''}`
                  : 'No target set'}
              </span>
            ) : selectedStrikes.length > 0 ? (
              <>
                <span className="msf-section-active">
                  {selectedStrikes.length === 1
                    ? intradayPresets.strike[selectedStrikes[0]].label
                    : `${selectedStrikes.length} selected`}
                </span>
                <button type="button" className="msf-section-clear" onClick={() => setSelectedStrikes([])}>
                  Clear
                </button>
              </>
            ) : (
              <span className="msf-section-count">None selected</span>
            )}
          </div>
        </div>

        {/* Sub-mode toggle: moneyness bands vs. an absolute target price */}
        <div className="msf-mode-toggle" data-tour="strike-mode-toggle" style={{ marginBottom: 10 }}>
          {[['bands', '% ITM/OTM'], ['target', 'Target price']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setStrikeMode(key)}
              className={`msf-mode-segment ${strikeMode === key ? 'msf-selected' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        {strikeMode === 'target' ? (
          <TargetPriceStrikeSection
            ticker={activeTicker}
            currentPrice={currentPrice}
            priceLoading={priceLoading}
            priceError={priceError}
            onRetryPrice={handleRetryPrice}
            targetPrice={targetPrice}
            onTargetPriceChange={setTargetPrice}
            bufferPct={bufferPct}
            onBufferPctChange={setBufferPct}
            isMobile
          />
        ) : (
          <>
            <div className="msf-pill-row-wrap">
              <div className="msf-pill-row" ref={strikeRowRef}>
                {Object.entries(intradayPresets.strike).map(([key, preset]) => {
                  const isSelected = selectedStrikes.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      data-default-pill={key === 'atm' ? 'true' : undefined}
                      onClick={() => toggle(selectedStrikes, setSelectedStrikes, key)}
                      className={`msf-pill ${isSelected ? 'msf-selected' : ''}`}
                      title={preset.tooltip}
                    >
                      <span className="msf-pill-label">{preset.label}</span>
                      <span className="msf-pill-sublabel">{adjustDescription(preset.description)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="msf-strike-caption">
              <span>← in the money</span>
              <span>out of the money →</span>
            </div>
          </>
        )}
      </div>

      {/* CTA stack: Add to queue / Save as preset / Run X Scenarios */}
      <div className="msf-cta-wrap">
        <button
          type="button"
          className="msf-cta"
          onClick={handleRunNow}
          disabled={!isFormValid() || loading}
        >
          <Play className="msf-cta-icon" style={{ fill: 'currentColor' }} />
          {totalScenarios > 1 ? `Run ${totalScenarios} scenarios` : 'Run scenario'}
        </button>
        <button
          type="button"
          className="msf-cta msf-cta-secondary"
          onClick={handleAddToQueue}
          disabled={!isFormValid() || loading}
        >
          <PlusCircle className="msf-cta-icon" />
          {totalScenarios > 1 ? `Add ${totalScenarios} to queue` : 'Add to queue'}
          {queueCount > 0 && <span className="msf-cta-badge">{queueCount} queued</span>}
        </button>
        <button
          type="button"
          className="msf-save-btn"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
        >
          Save as preset
        </button>
        <p className="msf-cta-hint">
          Tap any pill to toggle. The count in each section header updates as you select;
          "Clear" wipes the section in one tap.
        </p>
      </div>

      {/* Load preset slide-up sheet */}
      <div
        className={`msf-sheet-backdrop ${isPresetSheetOpen ? 'msf-sheet-open' : ''}`}
        onClick={() => setIsPresetSheetOpen(false)}
      />
      <div className={`msf-sheet ${isPresetSheetOpen ? 'msf-sheet-open' : ''}`}>
        <div className="msf-sheet-handle-bar" />
        <div className="msf-sheet-header">
          <span className="msf-sheet-title">Saved presets</span>
          <button
            type="button"
            className="msf-sheet-close"
            onClick={() => setIsPresetSheetOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="msf-sheet-body">
          <ScreenerSavedParams
            savedParams={savedParams}
            onLoad={(p) => { if (onLoadParam) onLoadParam(p); setIsPresetSheetOpen(false); }}
            onDelete={onDeleteParam}
            onAddToQueue={(p) => { if (onAddParamToQueue) onAddParamToQueue(p); setIsPresetSheetOpen(false); }}
            compact
          />
        </div>
      </div>
    </div>
  );
});

export default MobileZeroDTEForm;
